import { useEffect, useRef, useState } from "react";
import type { ServiceStaff } from "@/types/appointments";
import { cn } from "@/lib/utils";
import { AppointmentSlotCard } from "./AppointmentSlotCard";
import type { AppointmentCalendarItem, CalendarView } from "./types";
import { getAppointmentAccent, getSlotKey } from "./utils";

interface AppointmentsWeekGridProps {
  isLoading: boolean;
  hasLoadedOnce: boolean;
  selectedDate: Date;
  view: CalendarView;
  visibleDays: Date[];
  timeSlots: string[];
  appointmentsBySlot: Map<string, AppointmentCalendarItem[]>;
  serviceStaffList: ServiceStaff[];
  onAppointmentClick: (appointmentId: string) => void;
}

export const AppointmentsWeekGrid = ({
  isLoading,
  hasLoadedOnce,
  selectedDate,
  view,
  visibleDays,
  timeSlots,
  appointmentsBySlot,
  serviceStaffList,
  onAppointmentClick,
}: AppointmentsWeekGridProps) => {
  const [now, setNow] = useState(() => new Date());
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const lastAutoScrollKeyRef = useRef<string | null>(null);
  const hourRowHeight = 56;
  const isDailyView = view === "day";
  const gridColumnsClass = isDailyView
    ? "grid-cols-[5rem_minmax(18rem,1fr)]"
    : "grid-cols-[5rem_repeat(7,minmax(8rem,1fr))]";
  const dayColumnsSpanClass = isDailyView ? "col-span-1" : "col-span-7";
  const gridMinWidthClass = isDailyView ? "min-w-[23rem]" : "min-w-[720px]";

  useEffect(() => {
    let intervalId: number | undefined;

    const refreshNow = () => {
      setNow(new Date());
    };

    refreshNow();

    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    const initialTimeout = window.setTimeout(() => {
      refreshNow();
      intervalId = window.setInterval(refreshNow, 60_000);
    }, msUntilNextMinute);

    return () => {
      window.clearTimeout(initialTimeout);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  const today = now.toDateString();
  const selectedDay = selectedDate.toDateString();
  const currentDayIndex = visibleDays.findIndex(
    (day) => day.toDateString() === today
  );
  const showCurrentTime = currentDayIndex >= 0;
  const currentTimeTop =
    ((now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60) / 60) *
    hourRowHeight;
  const visibleRangeKey = visibleDays
    .map((day) => day.toDateString())
    .join("|");
  const currentHour = now.getHours() % 12 || 12;
  const currentTimeLabel = `${String(currentHour).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;

  useEffect(() => {
    if (!showCurrentTime) {
      lastAutoScrollKeyRef.current = null;
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const autoScrollKey = `${view}-${visibleRangeKey}`;
    if (lastAutoScrollKeyRef.current === autoScrollKey) {
      return;
    }

    lastAutoScrollKeyRef.current = autoScrollKey;

    const frameId = window.requestAnimationFrame(() => {
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      const offset = Math.max(scrollContainer.clientHeight * 0.3, 120);
      const nextScrollTop = Math.max(currentTimeTop - headerHeight - offset, 0);

      scrollContainer.scrollTo({
        top: nextScrollTop,
        behavior: "smooth",
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentTimeTop, showCurrentTime, view, visibleRangeKey]);

  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-16">
        <div className="text-center">
          <div className="z-body-md text-foreground">Loading calendar...</div>
          <div className="z-secondary mt-1 text-muted-foreground">
            Fetching the latest appointment schedule.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-full overflow-auto"
      aria-busy={isLoading}
    >
      {isLoading && hasLoadedOnce ? (
        <div className="pointer-events-none absolute right-3 top-3 z-20">
          <div className="rounded-full border border-border bg-card px-3 py-1 shadow-sm">
            <span className="z-caption text-muted-foreground">
              Updating schedule…
            </span>
          </div>
        </div>
      ) : null}

      <div className={gridMinWidthClass}>
        <div
          ref={headerRef}
          className={cn(
            "sticky top-0 z-10 grid border-b border-border bg-card",
            gridColumnsClass
          )}
        >
          <div className="border-r border-border border-t-2 border-t-transparent px-2 py-3.5" />
          {visibleDays.map((day) => {
            const isToday = day.toDateString() === today;
            const isSelectedDay = day.toDateString() === selectedDay;

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "border-r border-border border-t-2 px-3 py-3.5 last:border-r-0",
                  isSelectedDay
                    ? "border-t-primary bg-primary/10"
                    : isToday
                      ? "border-t-border bg-accent/30"
                      : "border-t-transparent"
                )}
              >
                {isDailyView ? (
                  <div className="flex flex-col items-start gap-0.5 text-left">
                    <span
                      className={cn(
                        "z-caption uppercase text-muted-foreground",
                        isSelectedDay
                          ? "text-primary/75"
                          : isToday
                            ? "text-foreground/70"
                            : "text-muted-foreground"
                      )}
                    >
                      {day.toLocaleDateString("en-US", { weekday: "long" })}
                    </span>
                    <span
                      className={cn(
                        "z-h2 z-numeric leading-none font-extrabold",
                        isSelectedDay ? "text-primary" : "text-foreground"
                      )}
                    >
                      {day.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "flex items-baseline justify-center gap-2",
                      isSelectedDay ? "text-primary" : "text-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "z-body-md",
                        isSelectedDay
                          ? "text-primary"
                          : isToday
                            ? "text-foreground"
                            : "text-foreground/75"
                      )}
                    >
                      {day.toLocaleDateString("en-US", {
                        weekday: "short",
                      })}
                    </span>
                    <span
                      className={cn(
                        "z-h2 z-numeric leading-none font-extrabold",
                        isSelectedDay ? "text-primary" : "text-foreground"
                      )}
                    >
                      {String(day.getDate()).padStart(2, "0")}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="relative">
          {showCurrentTime ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0"
              style={{ top: `${currentTimeTop}px` }}
            >
              <div className={cn("grid items-center", gridColumnsClass)}>
                <div className="flex justify-end pr-3">
                  <div className="rounded-full border border-border/70 bg-card px-2 py-0.5 shadow-sm">
                    <span className="z-hint z-numeric text-foreground/65">
                      {currentTimeLabel}
                    </span>
                  </div>
                </div>

                <div
                  className={cn("relative h-px bg-border", dayColumnsSpanClass)}
                >
                  <div
                    className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-muted-foreground/70 shadow-sm"
                    style={{
                      left: `${((currentDayIndex + 0.5) / visibleDays.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {timeSlots.map((slot, slotIndex) => (
            <div
              key={slot}
              className={cn(
                "relative grid min-h-14 border-b border-border/80 last:border-b-0",
                gridColumnsClass
              )}
            >
              <div className="border-r border-border px-2 py-2 text-right z-caption z-numeric text-muted-foreground">
                {slot}
              </div>

              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dotted border-border/70"
              />

              {visibleDays.map((day) => {
                const appointments =
                  appointmentsBySlot.get(getSlotKey(day, slotIndex)) ?? [];

                return (
                  <div
                    key={`${day.toISOString()}-${slot}`}
                    className="border-r border-border/80 px-1.5 py-1.5 last:border-r-0 sm:px-2 sm:py-2"
                  >
                    <div className="flex flex-col gap-1.5">
                      {appointments.map((appointment) => (
                        <AppointmentSlotCard
                          key={appointment.id}
                          appointment={appointment}
                          accent={getAppointmentAccent(
                            appointment.staffId,
                            serviceStaffList
                          )}
                          onClick={onAppointmentClick}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
